/**
 * Service health view: version, uptime and live call-state counters from
 * the public health endpoint, auto-refreshed.
 */

import { useEffect, useState } from 'react';
import { useTranslate } from 'react-admin';
import { Box, Card, CardContent, Chip, Grid, Typography } from '@mui/material';
import { httpClient } from '@/utils/httpClient';

const REFRESH_INTERVAL_MS = 5000;

interface HealthInfo {
  status: string;
  version?: string;
  uptimeSeconds?: number;
  calls?: Record<string, number>;
}

const formatUptime = (seconds?: number): string => {
  if (!seconds && seconds !== 0) return '—';
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  if (days > 0) return `${days}d ${hours}h ${minutes}m`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
};

const COUNTER_KEYS = [
  'activeRooms',
  'busyParticipants',
  'ringingStaff',
  'queuedCalls',
  'watchedCalls',
  'pendingTimers',
];

export const HealthView = () => {
  const translate = useTranslate();
  const [health, setHealth] = useState<HealthInfo | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let active = true;
    const load = () =>
      httpClient
        .get<HealthInfo>('/health', { skipAuth: true })
        .then((response) => {
          if (!active) return;
          setHealth(response.data);
          setFailed(false);
        })
        .catch(() => {
          if (!active) return;
          setFailed(true);
        });
    load();
    const interval = setInterval(load, REFRESH_INTERVAL_MS);
    return () => {
      active = false;
      clearInterval(interval);
    };
  }, []);

  const ok = !failed && health?.status === 'ok';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Chip
          color={ok ? 'success' : 'error'}
          label={
            ok
              ? translate('pages.system.health.ok')
              : translate('pages.system.health.down')
          }
        />
        <Typography variant="body2" color="text.secondary">
          {translate('pages.system.health.version')}: {health?.version ?? '—'}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {translate('pages.system.health.uptime')}: {formatUptime(health?.uptimeSeconds)}
        </Typography>
      </Box>
      <Grid container spacing={2}>
        {COUNTER_KEYS.map((key) => (
          <Grid item xs={6} sm={4} md={2} key={key}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h4" sx={{ fontWeight: 700 }}>
                  {health?.calls?.[key] ?? '—'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {translate(`pages.system.health.counters.${key}`, { _: key })}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
      <Typography variant="caption" color="text.secondary">
        {translate('pages.system.health.refresh_note')}
      </Typography>
    </Box>
  );
};

export default HealthView;
